/**
 * Approvals Page — Admin trip lifecycle management
 *
 * Three sections:
 * 1. Pending Approval — trips with SUBMITTED flights, approve individually or all at once
 * 2. Ready to Complete — all flights approved, admin can finalize the trip
 * 3. Recently Completed — done trips with summary
 *
 * Admin can also inline-edit flight times before approving.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/services/api';
import { authFetch } from '@/services/authService';
import { API_BASE_URL } from '@/services/api';
import { adminCompleteTrip } from '@/services/tripService';
import { useToast } from '@/contexts/ToastContext';
import {
  CheckCircleIcon,
  ClockIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  PencilSquareIcon,
  CheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

interface Flight {
  id: number;
  legNumber: number;
  fromAirport: string;
  toAirport: string;
  date: string;
  tailNumber: string;
  actualAirTime: number | null;
  actualFlightTime: number | null;
  pilotNotes: string | null;
  status: string;
}

interface Trip {
  id: number;
  qNumber: string;
  startDate: string;
  endDate: string;
  tailNumber: string | null;
  aircraftType: string | null;
  status: string;
  flights: Flight[];
  crewMembers: Array<{ user: { firstName: string; lastName: string }; role: string }>;
}

type Tab = 'pending' | 'ready' | 'completed';

export default function ApprovalsPage() {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();

  const [trips, setTrips] = useState<Trip[]>([]);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState<number | null>(null);
  const [completing, setCompleting] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>('pending');
  const [expandedTrips, setExpandedTrips] = useState<Set<number>>(new Set());

  // Inline editing state
  const [editingFlight, setEditingFlight] = useState<number | null>(null);
  const [editForm, setEditForm] = useState({ actualAirTime: '', actualFlightTime: '', pilotNotes: '' });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await api.get<{ data: Trip[] }>('/trips');
      setTrips(res.data);
    } catch {
      showError('Load failed', 'Could not load trips');
    } finally {
      setLoading(false);
    }
  };

  // --- Categorize trips ---
  const tripsWithSubmitted = trips.filter(t =>
    t.flights.some(f => f.status === 'SUBMITTED')
  );

  const tripsReadyToComplete = trips.filter(t =>
    t.status !== 'COMPLETED' &&
    t.flights.length > 0 &&
    t.flights.every(f => f.status === 'APPROVED' || f.status === 'PAID' || f.status === 'COMPLETED')
  );

  const tripsCompleted = trips.filter(t => t.status === 'COMPLETED');

  const submittedCount = trips.reduce(
    (sum, t) => sum + t.flights.filter(f => f.status === 'SUBMITTED').length, 0
  );

  // On initial load, select the first tab with content
  const [hasAutoSelected, setHasAutoSelected] = useState(false);
  useEffect(() => {
    if (hasAutoSelected || loading) return;
    if (tripsWithSubmitted.length > 0) {
      setActiveTab('pending');
    } else if (tripsReadyToComplete.length > 0) {
      setActiveTab('ready');
    } else if (tripsCompleted.length > 0) {
      setActiveTab('completed');
    }
    setHasAutoSelected(true);
  }, [loading, hasAutoSelected, tripsWithSubmitted.length, tripsReadyToComplete.length, tripsCompleted.length]);

  // --- Actions ---

  const handleApprove = async (flightId: number) => {
    setApproving(flightId);
    try {
      const response = await authFetch(`${API_BASE_URL}/flights/trip/${flightId}/approve`, {
        method: 'POST',
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to approve');
      }
      showSuccess('Flight approved');
      await loadData();
    } catch (err: any) {
      showError('Approval failed', err.message);
    } finally {
      setApproving(null);
    }
  };

  const handleApproveAll = async (trip: Trip) => {
    const submittedFlights = trip.flights.filter(f => f.status === 'SUBMITTED');
    setApproving(trip.id);
    try {
      for (const flight of submittedFlights) {
        const response = await authFetch(`${API_BASE_URL}/flights/trip/${flight.id}/approve`, {
          method: 'POST',
        });
        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.message || `Failed to approve leg ${flight.legNumber}`);
        }
      }
      showSuccess(`All ${submittedFlights.length} flights approved`);
      await loadData();
    } catch (err: any) {
      showError('Approval failed', err.message);
      await loadData();
    } finally {
      setApproving(null);
    }
  };

  const handleCompleteTrip = async (tripId: number) => {
    setCompleting(tripId);
    try {
      const result = await adminCompleteTrip(tripId);
      showSuccess(
        'Trip completed',
        `${result.totalFlights} flights, ${result.totalFlightTime.toFixed(1)}h total`
      );
      await loadData();
    } catch (err: any) {
      showError('Completion failed', err.message);
    } finally {
      setCompleting(null);
    }
  };

  const startEditing = (flight: Flight) => {
    setEditingFlight(flight.id);
    setEditForm({
      actualAirTime: flight.actualAirTime?.toString() || '',
      actualFlightTime: flight.actualFlightTime?.toString() || '',
      pilotNotes: flight.pilotNotes || '',
    });
  };

  const cancelEditing = () => {
    setEditingFlight(null);
  };

  const saveEditing = async (flightId: number) => {
    if (!editForm.actualAirTime || !editForm.actualFlightTime) {
      showError('Missing data', 'Air time and flight time are required');
      return;
    }
    try {
      const response = await authFetch(`${API_BASE_URL}/flights/trip/${flightId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          actualAirTime: parseFloat(editForm.actualAirTime),
          actualFlightTime: parseFloat(editForm.actualFlightTime),
          pilotNotes: editForm.pilotNotes,
          reason: 'Admin correction before approval',
        }),
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to update');
      }
      showSuccess('Flight updated');
      setEditingFlight(null);
      await loadData();
    } catch (err: any) {
      showError('Update failed', err.message);
    }
  };

  const toggleExpanded = (tripId: number) => {
    setExpandedTrips(prev => {
      const next = new Set(prev);
      if (next.has(tripId)) {
        next.delete(tripId);
      } else {
        next.add(tripId);
      }
      return next;
    });
  };

  // --- Helpers ---

  const formatDate = (d: string) => {
    const [y, m, day] = d.slice(0, 10).split('-').map(Number);
    return new Date(y, m - 1, day).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
  };

  const crewNames = (trip: Trip) =>
    trip.crewMembers.map(cm => `${cm.user.firstName} ${cm.user.lastName}`).join(', ');

  const totalFlightTime = (trip: Trip) =>
    trip.flights.reduce((sum, f) => sum + (f.actualFlightTime || 0), 0);

  // --- Render ---

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const tabs: { key: Tab; label: string; count: number }[] = [
    { key: 'pending', label: 'Pending Approval', count: submittedCount },
    { key: 'ready', label: 'Ready to Complete', count: tripsReadyToComplete.length },
    { key: 'completed', label: 'Completed', count: tripsCompleted.length },
  ];

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-foreground">Approvals</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Review, approve, and complete trips
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b border-border">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              }`}
            >
              {tab.label}
              {tab.count > 0 && (
                <span className={`ml-2 px-1.5 py-0.5 text-xs rounded-full ${
                  activeTab === tab.key
                    ? 'bg-primary/10 text-primary'
                    : 'bg-muted text-muted-foreground'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* === PENDING APPROVAL TAB === */}
        {activeTab === 'pending' && (
          <>
            {tripsWithSubmitted.length === 0 ? (
              <EmptyState
                icon={<CheckCircleIcon className="w-12 h-12 text-green-500" />}
                title="All caught up"
                subtitle="No flights waiting for approval."
              />
            ) : (
              <div className="space-y-4">
                {tripsWithSubmitted.map(trip => {
                  const submitted = trip.flights.filter(f => f.status === 'SUBMITTED');
                  return (
                    <div key={trip.id} className="bg-card border border-border rounded-lg overflow-hidden">
                      {/* Trip header */}
                      <div className="px-5 py-3 bg-muted/30 flex items-center justify-between">
                        <div
                          className="flex items-center gap-4 cursor-pointer hover:opacity-80"
                          onClick={() => navigate(`/trips/${trip.id}`)}
                        >
                          <span className="font-mono font-semibold text-foreground">{trip.qNumber}</span>
                          <span className="text-sm text-muted-foreground">{trip.tailNumber}</span>
                          <span className="text-xs text-muted-foreground">
                            {formatDate(trip.startDate)} – {formatDate(trip.endDate)}
                          </span>
                          <span className="text-xs text-muted-foreground hidden sm:inline">
                            {crewNames(trip)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          {submitted.length > 1 && (
                            <button
                              onClick={() => handleApproveAll(trip)}
                              disabled={approving === trip.id}
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded-lg transition-colors disabled:opacity-50"
                            >
                              {approving === trip.id ? (
                                <span className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent" />
                              ) : (
                                <CheckCircleIcon className="w-3.5 h-3.5" />
                              )}
                              Approve All ({submitted.length})
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Flight rows */}
                      <div className="divide-y divide-border">
                        {submitted
                          .sort((a, b) => a.legNumber - b.legNumber)
                          .map(flight => (
                            <div key={flight.id} className="px-5 py-4">
                              {editingFlight === flight.id ? (
                                /* Inline edit mode */
                                <div className="space-y-3">
                                  <div className="flex items-center gap-3">
                                    <span className="text-sm font-medium text-foreground">
                                      Leg {flight.legNumber}: {flight.fromAirport} → {flight.toAirport}
                                    </span>
                                    <span className="text-xs text-muted-foreground">{formatDate(flight.date)}</span>
                                  </div>
                                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                    <div>
                                      <label className="block text-xs text-muted-foreground mb-1">Air Time (h)</label>
                                      <input
                                        type="number"
                                        step="0.1"
                                        min="0"
                                        value={editForm.actualAirTime}
                                        onChange={e => setEditForm({ ...editForm, actualAirTime: e.target.value })}
                                        className="w-full px-2 py-1.5 text-sm border border-border bg-background rounded-md focus:ring-2 focus:ring-primary"
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs text-muted-foreground mb-1">Flight Time (h)</label>
                                      <input
                                        type="number"
                                        step="0.1"
                                        min="0"
                                        value={editForm.actualFlightTime}
                                        onChange={e => setEditForm({ ...editForm, actualFlightTime: e.target.value })}
                                        className="w-full px-2 py-1.5 text-sm border border-border bg-background rounded-md focus:ring-2 focus:ring-primary"
                                      />
                                    </div>
                                    <div className="col-span-2 sm:col-span-1">
                                      <label className="block text-xs text-muted-foreground mb-1">Notes</label>
                                      <input
                                        type="text"
                                        value={editForm.pilotNotes}
                                        onChange={e => setEditForm({ ...editForm, pilotNotes: e.target.value })}
                                        className="w-full px-2 py-1.5 text-sm border border-border bg-background rounded-md focus:ring-2 focus:ring-primary"
                                      />
                                    </div>
                                  </div>
                                  <div className="flex gap-2">
                                    <button
                                      onClick={() => saveEditing(flight.id)}
                                      className="flex items-center gap-1 px-3 py-1.5 bg-primary text-primary-foreground text-xs font-medium rounded-md hover:bg-primary/90"
                                    >
                                      <CheckIcon className="w-3.5 h-3.5" />
                                      Save
                                    </button>
                                    <button
                                      onClick={cancelEditing}
                                      className="flex items-center gap-1 px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground"
                                    >
                                      <XMarkIcon className="w-3.5 h-3.5" />
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                /* Display mode */
                                <div className="flex items-center justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-3">
                                      <span className="text-sm font-medium text-foreground">
                                        Leg {flight.legNumber}: {flight.fromAirport} → {flight.toAirport}
                                      </span>
                                      <span className="text-xs text-muted-foreground">{formatDate(flight.date)}</span>
                                    </div>
                                    <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
                                      {flight.actualAirTime != null && (
                                        <span>Air: {flight.actualAirTime}h</span>
                                      )}
                                      {flight.actualFlightTime != null && (
                                        <span>Flight: {flight.actualFlightTime}h</span>
                                      )}
                                      {flight.pilotNotes && (
                                        <span className="truncate max-w-xs" title={flight.pilotNotes}>
                                          Note: {flight.pilotNotes}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <button
                                      onClick={() => startEditing(flight)}
                                      className="p-1.5 text-muted-foreground hover:text-foreground rounded-md hover:bg-muted transition-colors"
                                      title="Edit flight data"
                                    >
                                      <PencilSquareIcon className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => handleApprove(flight.id)}
                                      disabled={approving === flight.id}
                                      className="flex items-center gap-1.5 px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                                    >
                                      {approving === flight.id ? (
                                        <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                                      ) : (
                                        <CheckCircleIcon className="w-4 h-4" />
                                      )}
                                      Approve
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* === READY TO COMPLETE TAB === */}
        {activeTab === 'ready' && (
          <>
            {tripsReadyToComplete.length === 0 ? (
              <EmptyState
                icon={<ClockIcon className="w-12 h-12 text-muted-foreground" />}
                title="No trips ready"
                subtitle="Approve all flights in a trip to see it here."
              />
            ) : (
              <div className="space-y-4">
                {tripsReadyToComplete.map(trip => {
                  const isExpanded = expandedTrips.has(trip.id);
                  const total = totalFlightTime(trip);

                  return (
                    <div key={trip.id} className="bg-card border border-border rounded-lg overflow-hidden">
                      {/* Trip header */}
                      <div className="px-5 py-4 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <button
                            onClick={() => toggleExpanded(trip.id)}
                            className="text-muted-foreground hover:text-foreground"
                          >
                            {isExpanded
                              ? <ChevronDownIcon className="w-4 h-4" />
                              : <ChevronRightIcon className="w-4 h-4" />
                            }
                          </button>
                          <div
                            className="cursor-pointer hover:opacity-80"
                            onClick={() => navigate(`/trips/${trip.id}`)}
                          >
                            <div className="flex items-center gap-3">
                              <span className="font-mono font-semibold text-foreground">{trip.qNumber}</span>
                              <span className="text-sm text-muted-foreground">{trip.tailNumber}</span>
                              <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                                {trip.flights.length} legs approved
                              </span>
                            </div>
                            <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
                              <span>{formatDate(trip.startDate)} – {formatDate(trip.endDate)}</span>
                              <span>{total.toFixed(1)}h total</span>
                              <span>{crewNames(trip)}</span>
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => handleCompleteTrip(trip.id)}
                          disabled={completing === trip.id}
                          className="flex items-center gap-2 px-5 py-2.5 bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-semibold rounded-lg transition-colors disabled:opacity-50"
                        >
                          {completing === trip.id ? (
                            <>
                              <span className="animate-spin rounded-full h-4 w-4 border-2 border-primary-foreground border-t-transparent" />
                              Completing...
                            </>
                          ) : (
                            <>
                              <CheckCircleIcon className="w-5 h-5" />
                              Complete Trip
                            </>
                          )}
                        </button>
                      </div>

                      {/* Expanded flight details */}
                      {isExpanded && (
                        <div className="border-t border-border divide-y divide-border">
                          {trip.flights
                            .sort((a, b) => a.legNumber - b.legNumber)
                            .map(flight => (
                              <div key={flight.id} className="px-5 py-3 flex items-center justify-between bg-muted/20">
                                <div>
                                  <span className="text-sm font-medium text-foreground">
                                    Leg {flight.legNumber}: {flight.fromAirport} → {flight.toAirport}
                                  </span>
                                  <span className="text-xs text-muted-foreground ml-3">{formatDate(flight.date)}</span>
                                </div>
                                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                                  {flight.actualAirTime != null && <span>Air: {flight.actualAirTime}h</span>}
                                  {flight.actualFlightTime != null && <span>Flight: {flight.actualFlightTime}h</span>}
                                  <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                                    {flight.status.replace('_', ' ')}
                                  </span>
                                </div>
                              </div>
                            ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* === COMPLETED TAB === */}
        {activeTab === 'completed' && (
          <>
            {tripsCompleted.length === 0 ? (
              <EmptyState
                icon={<CheckCircleIcon className="w-12 h-12 text-muted-foreground" />}
                title="No completed trips"
                subtitle="Completed trips will appear here."
              />
            ) : (
              <div className="space-y-2">
                {tripsCompleted
                  .sort((a, b) => b.endDate.localeCompare(a.endDate))
                  .map(trip => {
                    const isExpanded = expandedTrips.has(trip.id);
                    const total = totalFlightTime(trip);

                    return (
                      <div key={trip.id} className="bg-card border border-border rounded-lg overflow-hidden">
                        <div className="px-5 py-3 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => toggleExpanded(trip.id)}
                              className="text-muted-foreground hover:text-foreground"
                            >
                              {isExpanded
                                ? <ChevronDownIcon className="w-4 h-4" />
                                : <ChevronRightIcon className="w-4 h-4" />
                              }
                            </button>
                            <span
                              className="font-mono font-medium text-foreground text-sm cursor-pointer hover:underline"
                              onClick={() => navigate(`/trips/${trip.id}`)}
                            >
                              {trip.qNumber}
                            </span>
                            <span className="text-sm text-muted-foreground">{trip.tailNumber}</span>
                            <span className="text-xs text-muted-foreground">
                              {formatDate(trip.startDate)} – {formatDate(trip.endDate)}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {trip.flights.length} legs, {total.toFixed(1)}h
                            </span>
                          </div>
                          <span className="px-2 py-0.5 text-xs rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400">
                            Completed
                          </span>
                        </div>

                        {isExpanded && (
                          <div className="border-t border-border divide-y divide-border">
                            {trip.flights
                              .sort((a, b) => a.legNumber - b.legNumber)
                              .map(flight => (
                                <div key={flight.id} className="px-5 py-2.5 flex items-center justify-between bg-muted/10 text-sm">
                                  <span className="text-foreground">
                                    Leg {flight.legNumber}: {flight.fromAirport} → {flight.toAirport}
                                  </span>
                                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                                    <span>{formatDate(flight.date)}</span>
                                    {flight.actualAirTime != null && <span>Air: {flight.actualAirTime}h</span>}
                                    {flight.actualFlightTime != null && <span>Flight: {flight.actualFlightTime}h</span>}
                                  </div>
                                </div>
                              ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function EmptyState({ icon, title, subtitle }: { icon: React.ReactNode; title: string; subtitle: string }) {
  return (
    <div className="bg-card border border-border rounded-lg px-5 py-16 text-center">
      <div className="mx-auto mb-3 flex justify-center">{icon}</div>
      <h3 className="text-lg font-medium text-foreground">{title}</h3>
      <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>
    </div>
  );
}
